import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  CheckCircle2Icon,
  EditIcon,
  MoreVerticalIcon,
  XCircleIcon,
  ClockIcon,
  UserXIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'

interface Appointment {
  id: number
  status: string
  upcoming: boolean
  scheduled_at: string
}

interface AppointmentActionsMenuProps {
  appointment: Appointment
  onEdit?: () => void
}

export function AppointmentActionsMenu({ appointment, onEdit }: AppointmentActionsMenuProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showCompleteDialog, setShowCompleteDialog] = useState(false)
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [cancellationReason, setCancellationReason] = useState('')
  const [completionNotes, setCompletionNotes] = useState('')
  const [newDate, setNewDate] = useState<Date | undefined>(new Date(appointment.scheduled_at))
  const [newTime, setNewTime] = useState(format(new Date(appointment.scheduled_at), 'HH:mm'))

  const canEdit = appointment.upcoming && appointment.status !== 'cancelled'
  const canComplete = appointment.status === 'scheduled' || appointment.status === 'confirmed'
  const canCancel = appointment.status !== 'cancelled' && appointment.status !== 'completed'
  const canConfirm = appointment.status === 'scheduled'
  const canMarkNoShow = appointment.status === 'scheduled' || appointment.status === 'confirmed'

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token')

    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    }
  }

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      const response = await fetch(`/admin/appointments/${appointment.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'confirmed' }),
      })

      const result = await response.json()
      if (response.ok) {
        toast.success('Appointment confirmed')
        router.reload({ only: ['appointments'] })
      } else {
        toast.error(result.errors?.join(', ') || 'Failed to confirm appointment')
      }
    } catch (error) {
      toast.error('An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = async () => {
    if (!cancellationReason || cancellationReason.length < 10) {
      toast.error('Cancellation reason must be at least 10 characters')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await fetch(`/admin/appointments/${appointment.id}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ cancellation_reason: cancellationReason }),
      })

      const result = await response.json()
      if (response.ok) {
        toast.success('Appointment cancelled')
        setShowCancelDialog(false)
        setCancellationReason('')
        router.reload({ only: ['appointments'] })
      } else {
        toast.error(result.errors?.join(', ') || 'Failed to cancel appointment')
      }
    } catch (error) {
      toast.error('An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleComplete = async () => {
    setIsSubmitting(true)
    try {
      const response = await fetch(`/admin/appointments/${appointment.id}/complete`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes: completionNotes || null }),
      })

      const result = await response.json()
      if (response.ok) {
        toast.success('Appointment marked as complete')
        setShowCompleteDialog(false)
        setCompletionNotes('')
        router.reload({ only: ['appointments'] })
      } else {
        toast.error(result.errors?.join(', ') || 'Failed to complete appointment')
      }
    } catch (error) {
      toast.error('An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReschedule = async () => {
    if (!newDate) {
      toast.error('Please select a date')
      return
    }

    if (!newTime) {
      toast.error('Please select a time')
      return
    }

    setIsSubmitting(true)
    try {
      const [hours, minutes] = newTime.split(':')
      const scheduledAt = new Date(newDate)
      scheduledAt.setHours(parseInt(hours), parseInt(minutes), 0, 0)

      const response = await fetch(`/admin/appointments/${appointment.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ scheduled_at: scheduledAt.toISOString() }),
      })

      const result = await response.json()
      if (response.ok) {
        toast.success('Appointment rescheduled')
        setShowRescheduleDialog(false)
        router.reload({ only: ['appointments'] })
      } else {
        toast.error(result.errors?.join(', ') || 'Failed to reschedule appointment')
      }
    } catch (error) {
      toast.error('An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNoShow = async () => {
    setIsSubmitting(true)
    try {
      const response = await fetch(`/admin/appointments/${appointment.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'no_show' }),
      })

      const result = await response.json()
      if (response.ok) {
        toast.success('Appointment marked as no-show')
        router.reload({ only: ['appointments'] })
      } else {
        toast.error(result.errors?.join(', ') || 'Failed to update appointment')
      }
    } catch (error) {
      toast.error('An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreVerticalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {canEdit && onEdit && (
            <DropdownMenuItem onClick={onEdit}>
              <EditIcon className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}

          {canEdit && (
            <DropdownMenuItem onClick={() => setShowRescheduleDialog(true)}>
              <ClockIcon className="mr-2 h-4 w-4" />
              Reschedule
            </DropdownMenuItem>
          )}

          {canConfirm && (
            <DropdownMenuItem onClick={handleConfirm} disabled={isSubmitting}>
              <CheckCircle2Icon className="mr-2 h-4 w-4" />
              Confirm
            </DropdownMenuItem>
          )}

          {canComplete && (
            <DropdownMenuItem onClick={() => setShowCompleteDialog(true)}>
              <CheckCircle2Icon className="mr-2 h-4 w-4" />
              Mark Complete
            </DropdownMenuItem>
          )}

          {canMarkNoShow && (
            <DropdownMenuItem onClick={handleNoShow} disabled={isSubmitting}>
              <UserXIcon className="mr-2 h-4 w-4" />
              Mark No-Show
            </DropdownMenuItem>
          )}

          {canCancel && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowCancelDialog(true)}
                className="text-destructive focus:text-destructive"
              >
                <XCircleIcon className="mr-2 h-4 w-4" />
                Cancel Appointment
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Appointment</DialogTitle>
            <DialogDescription>
              Please provide a reason for cancelling this appointment. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancellation_reason">Cancellation Reason *</Label>
              <Textarea
                id="cancellation_reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Enter reason for cancellation (minimum 10 characters)"
                rows={3}
              />
              {cancellationReason && cancellationReason.length < 10 && (
                <p className="text-sm text-destructive">
                  Minimum 10 characters required ({cancellationReason.length}/10)
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCancelDialog(false)
                setCancellationReason('')
              }}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancel}
              disabled={isSubmitting || cancellationReason.length < 10}
            >
              {isSubmitting ? 'Cancelling...' : 'Cancel Appointment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Appointment</DialogTitle>
            <DialogDescription>
              Mark this appointment as completed. You can optionally add clinical notes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="completion_notes">Clinical Notes (Optional)</Label>
              <Textarea
                id="completion_notes"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Add any notes about this appointment"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCompleteDialog(false)
                setCompletionNotes('')
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleComplete} disabled={isSubmitting}>
              {isSubmitting ? 'Completing...' : 'Mark Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule Appointment</DialogTitle>
            <DialogDescription>
              Select a new date and time for this appointment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !newDate && 'text-muted-foreground'
                    )}
                  >
                    {newDate ? format(newDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={newDate} onSelect={setNewDate} />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_time">New Time *</Label>
              <Input
                id="new_time"
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowRescheduleDialog(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleReschedule} disabled={isSubmitting || !newDate || !newTime}>
              {isSubmitting ? 'Rescheduling...' : 'Reschedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
